'use client';

import {
  Button,
  deriveAppliedFilters,
  FilterChip,
  Menu,
  MenuContent,
  MenuItem,
  MenuSearch,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuTrigger,
} from '@lulalife/design-system';
import { FilterLines } from '@lulalife/design-system/icons/line';
import { type ReactNode, useState } from 'react';
import { MenuScrollRegion } from './MenuScrollRegion';

/** The DS Table Filters panel's width. */
const PANEL_WIDTH = 256;
/** A values list scrolls past 320. */
const VALUES_MAX_HEIGHT = 320;
/** The DS Date Picker (Presets) panel's width. */
const DATE_PANEL_WIDTH = 202;

type ListFilterOption = { value: string; label: string };

export type ListFilterField = {
  key: string;
  label: string;
  /**
   * `multiple` draws checkboxes; `single` is a plain list where picking again clears it; `date`
   * draws the field's own `content` (a date presets panel) instead of an option list.
   */
  mode: 'multiple' | 'single' | 'date';
  options: ReadonlyArray<ListFilterOption>;
  /** Shows a search row over the values, with this placeholder. */
  valuesSearchPlaceholder?: string;
  /**
   * `multiple` mode without a value list (e.g. a WO ID): what's typed in the search row is
   * offered as a value to tick, beside the values already ticked. Needs `valuesSearchPlaceholder`.
   */
  freeText?: boolean;
  /** The values are still loading. */
  loading?: boolean;
  /** `date` mode's panel content. */
  content?: ReactNode;
};

export type ListFilterSelection = Record<string, string[]>;

type ListFiltersMenuProps = {
  fields: ReadonlyArray<ListFilterField>;
  selected: ListFilterSelection;
  onChange: (key: string, values: string[]) => void;
  /** Called when a field's values panel is opened, so its values can be loaded. */
  onFieldOpen?: (key: string) => void;
  disabled?: boolean;
  /** Prefix for the trigger's `data-testid` (`{prefix}.filters`). */
  testIdPrefix: string;
};

const matches = (text: string, needle: string) => text.toLowerCase().includes(needle);

type ValuesPanelProps = {
  field: ListFilterField;
  ticked: string[];
  onChange: (key: string, values: string[]) => void;
};

/** One field's values: checkboxes, a single-select list, or the field's own date panel. */
const ValuesPanel = ({ field, ticked, onChange }: ValuesPanelProps) => {
  const [query, setQuery] = useState('');
  if (field.mode === 'date') {
    // DS-GAP(date-picker-presets): the package has no Date Picker Type=Presets, so its surface
    // (radius 12, Shadow/md, 202 wide) is drawn on the submenu panel.
    return (
      <MenuSubContent
        width={DATE_PANEL_WIDTH}
        className="w-[202px]! rounded-datepicker-radius! border-datepicker-container-border! shadow-md!"
      >
        {field.content}
      </MenuSubContent>
    );
  }
  const needle = query.trim().toLowerCase();
  const options = field.freeText
    ? [...new Set([...ticked, query.trim()])]
        .filter((value) => value !== '')
        .map((value) => ({ value, label: value }))
    : field.options.filter((option) => needle === '' || matches(option.label, needle));
  const isLoading = Boolean(field.loading) && field.options.length === 0;
  const showMessage = !field.freeText && (isLoading || options.length === 0);
  return (
    <MenuSubContent width={PANEL_WIDTH} className="w-[256px]!">
      {field.valuesSearchPlaceholder ? (
        <MenuSearch value={query} onChange={setQuery} placeholder={field.valuesSearchPlaceholder} />
      ) : null}
      {showMessage ? (
        <p className="m-0 px-field-padding-x py-2 text-center text-sm text-field-placeholder">
          {isLoading ? 'Loading...' : 'No values found'}
        </p>
      ) : null}
      {/* 320 at most (as before), and less when the window's bottom edge is nearer. */}
      <MenuScrollRegion maxHeight={VALUES_MAX_HEIGHT} className="px-space-8">
        {options.map((option) =>
          field.mode === 'multiple' ? (
            <MenuItem
              key={option.value}
              type="checkbox"
              label={option.label}
              checked={ticked.includes(option.value)}
              onCheckedChange={(on) =>
                onChange(
                  field.key,
                  on ? [...ticked, option.value] : ticked.filter((value) => value !== option.value),
                )
              }
            />
          ) : (
            <MenuItem
              key={option.value}
              label={option.label}
              selected={ticked.includes(option.value)}
              onClick={() =>
                onChange(field.key, ticked.includes(option.value) ? [] : [option.value])
              }
            />
          ),
        )}
      </MenuScrollRegion>
    </MenuSubContent>
  );
};

/**
 * The DS Table Filters pattern, Filters › field › values (Properties 8043:11796, Service Requests
 * 8228:5005). Built from the DS `Menu` parts because the DS `Filters` only draws checkboxes, and
 * these lists also need single-select fields, searchable values, values that load on open and a
 * date presets panel.
 */
export const ListFiltersMenu = ({
  fields,
  selected,
  onChange,
  onFieldOpen,
  disabled = false,
  testIdPrefix,
}: ListFiltersMenuProps) => {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = fields.filter(
    (field) =>
      needle === '' ||
      matches(field.label, needle) ||
      field.options.some((option) => matches(option.label, needle)),
  );

  return (
    <Menu>
      <MenuTrigger>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          leftIcon={<FilterLines aria-hidden="true" />}
          data-testid={`${testIdPrefix}.filters`}
        >
          Filters
        </Button>
      </MenuTrigger>
      <MenuContent data-lula-ds="" align="start" width={PANEL_WIDTH} aria-label="Filters">
        <MenuSearch value={query} onChange={setQuery} placeholder="Search filters or values..." />
        {visible.length === 0 ? (
          <p className="px-field-padding-x py-2 text-center text-sm text-field-placeholder">
            No filters found
          </p>
        ) : null}
        <div className="flex flex-col px-space-8">
          {visible.map((field) => {
            const ticked = selected[field.key] ?? [];
            return (
              <MenuSub key={field.key}>
                {/* The DS submenu trigger takes no click handler; the capture tells the caller a
                    values panel is opening, so it can load the values.
                    DS-GAP(menu-item-badge-size): Figma's Menu / Item carries Badge Count md (20
                    high, 12/16; 8228:5005, Properties 8043:11796), but the package's BadgeCount
                    drops to sm (16, 10/14) from the md breakpoint up, and MenuSubTrigger has no
                    size prop. The wrapper keeps the md size at every width. */}
                <div
                  onClickCapture={() => onFieldOpen?.(field.key)}
                  className="[&_.badge]:md:h-badge-count-control-size-md [&_.badge]:md:min-w-badge-count-control-size-md [&_.badge]:md:badge-size-md"
                >
                  <MenuSubTrigger
                    label={field.label}
                    showBadge={field.mode === 'multiple' && ticked.length > 0}
                    badgeCount={ticked.length}
                  />
                </div>
                <ValuesPanel field={field} ticked={ticked} onChange={onChange} />
              </MenuSub>
            );
          })}
        </div>
      </MenuContent>
    </Menu>
  );
};

export type ListFilterChip = {
  key: string;
  label: string;
  value?: string;
  extraCount?: number;
  onRemove: () => void;
};

/** Chips for a field selection, one per field with values, as the DS derives them. */
export const selectionChips = (
  fields: ReadonlyArray<ListFilterField>,
  selected: ListFilterSelection,
  onChange: (key: string, values: string[]) => void,
): ListFilterChip[] =>
  deriveAppliedFilters({
    categories: fields.map(({ key, label, options }) => ({
      key,
      label,
      options: options.map((option) => ({ ...option })),
    })),
    selectedValues: selected,
  }).map((filter) => ({
    key: filter.key,
    label: filter.label,
    value: filter.value,
    extraCount: filter.extraCount,
    onRemove: () => onChange(filter.key, []),
  }));

type ListFilterChipsProps = {
  chips: ReadonlyArray<ListFilterChip>;
  onClear: () => void;
  /** Prefix for the row's and Clear's `data-testid`s. */
  testIdPrefix: string;
};

/**
 * The applied filters (Properties 8043:11139, Service Requests 8227:47578): "{Field}: {first
 * value}" chips with a +N badge, each removable, then Clear.
 */
export const ListFilterChips = ({ chips, onClear, testIdPrefix }: ListFilterChipsProps) => {
  if (chips.length === 0) return null;
  return (
    <div
      data-lula-ds=""
      className="flex flex-wrap items-center gap-space-8 font-sans"
      data-testid={`${testIdPrefix}.filter-chips`}
    >
      {chips.map((chip) => (
        <FilterChip
          key={chip.key}
          label={chip.label}
          value={chip.value}
          extraCount={chip.extraCount}
          onRemove={chip.onRemove}
        />
      ))}
      <Button
        type="button"
        variant="tertiary"
        onClick={onClear}
        data-testid={`${testIdPrefix}.filters-clear`}
      >
        Clear
      </Button>
    </div>
  );
};
